// src/locales/locales.ts
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// Import main translations
import enTranslation from './en/translation.json';
import ruTranslation from './ru/translation.json';

// Import menu translations
import enMenuTranslation from './en/menu.json';
import ruMenuTranslation from './ru/menu.json';

// Import clients section translations
import enClientsTranslation from './en/clients.json';
import ruClientsTranslation from './ru/clients.json';

// Import orders section translations
import enOrdersTranslation from './en/orders.json';
import ruOrdersTranslation from './ru/orders.json';

// Import analytics section translations
import enDashboardTranslation from './en/dashboard.json';
import ruDashboardTranslation from './ru/dashboard.json';
import enAnalyticsTranslation from './en/analytics.json';
import ruAnalyticsTranslation from './ru/analytics.json';
import enFunnelTranslation from './en/funnel.json';
import ruFunnelTranslation from './ru/funnel.json';
import enCustomerTranslation from './en/customer.json';
import ruCustomerTranslation from './ru/customer.json';

// Import promotions&discounts section translations
import enPromotionsTranslation from './en/promotions.json';
import ruPromotionsTranslation from './ru/promotions.json';

// Import errors translations
import enErrorsTranslation from './en/errors.json';
import ruErrorsTranslation from './ru/errors.json';

// Import common translations
import enCommonTranslation from './en/common.json';
import ruCommonTranslation from './ru/common.json';

// Import UI components translations
import enUITranslation from './en/ui.json';
import ruUITranslation from './ru/ui.json';

// Import marketing expenses translations
import enexpensesTranslation from './en/expenses.json';
import ruexpensesTranslation from './ru/expenses.json';

// Import profile translations
import enprofileTranslation from './en/profile.json';
import ruprofileTranslation from './ru/profile.json';

// Import currencies translations (добавлено)
import enCurrenciesTranslation from './en/currencies.json';
import ruCurrenciesTranslation from './ru/currencies.json';

// Import business types translations (добавлено)
import enBusinessTypesTranslation from './en/business_types.json';
import ruBusinessTypesTranslation from './ru/business_types.json';

// Проверка наличия файлов локализации перед использованием
const safelyImport = (module: any) => {
  try {
    return module;
  } catch (e) {
    console.warn('Warning: Failed to load translation module', e);
    return {}; // Return empty object as fallback
  }
};

const resources = {
  en: {
    translation: safelyImport(enTranslation),
    menu: safelyImport(enMenuTranslation),
    clients: safelyImport(enClientsTranslation),
    orders: safelyImport(enOrdersTranslation),
    dashboard: safelyImport(enDashboardTranslation),
    analytics: safelyImport(enAnalyticsTranslation),
    funnel: safelyImport(enFunnelTranslation),
    customer: safelyImport(enCustomerTranslation),
    promotions: safelyImport(enPromotionsTranslation),
    errors: safelyImport(enErrorsTranslation),
    common: safelyImport(enCommonTranslation),
    ui: safelyImport(enUITranslation),
    expenses: safelyImport(enexpensesTranslation),
    profile: safelyImport(enprofileTranslation),
    currencies: safelyImport(enCurrenciesTranslation), // Добавлено
    business_types: safelyImport(enBusinessTypesTranslation) // Добавлено
  },
  ru: {
    translation: safelyImport(ruTranslation),
    menu: safelyImport(ruMenuTranslation),
    clients: safelyImport(ruClientsTranslation),
    orders: safelyImport(ruOrdersTranslation),
    dashboard: safelyImport(ruDashboardTranslation),
    analytics: safelyImport(ruAnalyticsTranslation),
    funnel: safelyImport(ruFunnelTranslation),
    customer: safelyImport(ruCustomerTranslation),
    promotions: safelyImport(ruPromotionsTranslation),
    errors: safelyImport(ruErrorsTranslation),
    common: safelyImport(ruCommonTranslation),
    ui: safelyImport(ruUITranslation),
    expenses: safelyImport(ruexpensesTranslation),
    profile: safelyImport(ruprofileTranslation),
    currencies: safelyImport(ruCurrenciesTranslation), // Добавлено
    business_types: safelyImport(ruBusinessTypesTranslation) // Добавлено
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    // Настройки пространств имен
    ns: ['translation', 'menu', 'clients', 'orders', 'dashboard', 'analytics', 'funnel', 'customer', 'promotions', 
         'errors', 'common', 'ui', 'profile', 'expenses', 'currencies', 'business_types'],
    defaultNS: 'translation',

    interpolation: {
      escapeValue: false // Не экранировать HTML
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

// Добавляем функцию для принудительного переключения языка
export const changeLanguage = (lng: string) => {
  return i18n.changeLanguage(lng);
};

// Функция для проверки загрузки переводов (расширенная для диагностики)
export const checkTranslationsLoaded = () => {
  const loadedNamespaces = Object.keys(i18n.store.data);
  console.log('Loaded languages:', loadedNamespaces);
  
  if (loadedNamespaces.includes('en')) {
    console.log('English namespaces:', Object.keys(i18n.store.data.en));
  }
  
  if (loadedNamespaces.includes('ru')) {
    console.log('Russian namespaces:', Object.keys(i18n.store.data.ru));
    
    // Проверяем наличие ключей в пространстве имен profile
    if (i18n.store.data.ru.profile) {
      console.log('Russian profile keys:', Object.keys(i18n.store.data.ru.profile));
    }
    
    // Проверяем наличие ключей в пространстве имен business_types
    if (i18n.store.data.ru.business_types) {
      console.log('Russian business_types keys:', Object.keys(i18n.store.data.ru.business_types));
    }
    
    // Проверяем наличие ключей в пространстве имен currencies
    if (i18n.store.data.ru.currencies) {
      console.log('Russian currencies keys:', Object.keys(i18n.store.data.ru.currencies));
    }
    
    // Проверяем наличие ключей в пространстве имен common
    if (i18n.store.data.ru.common) {
      console.log('Russian common keys:', Object.keys(i18n.store.data.ru.common));
    }
  }
  
  return loadedNamespaces.length > 0;
};

export default i18n;